import { useLayoutEffect, useRef, useState } from "react"
import { loadAeroponicOriginalRecordMetadata } from "./virtualFarmerApi"

const validRecordId = value => typeof value === "string"
     && /^[1-9][0-9]{0,18}$/.exec(value)?.[0] === value
     && (value.length < 19 || value <= "9223372036854775807")
const validScope = value => (typeof value === "object" && value !== null) || typeof value === "symbol"
const grants = ["controls_hardware", "calibration_applied", "ec25_applied",
     "archive_export_authorized", "source_retention_authorized", "complete_physical_history"]
const TEXT = {
     de: {
          aria: "FEEDER Originalbelege", title: "Originalbeleg", load: "Originalmetadaten ansehen",
          loading: "Originalmetadaten werden gelesen …", auth: "Bitte anmelden, um private Originalmetadaten anzusehen.",
          ready: "Bitte zuerst den Kontozugang vervollständigen.", timeout: "Das Lesen der Originalmetadaten hat zu lange gedauert.",
          unavailable: "Für diesen Originalbeleg sind keine verlässlich zugeordneten Metadaten verfügbar.",
          error: "Die Originalmetadaten konnten nicht verlässlich gelesen werden.",
          record: "Original-Record-ID", receipt: "Original-Receipt-ID", utc: "Ursprünglicher Empfang (UTC)",
          clock: "Receipt-Uhrbasis", count: "Records im ursprünglichen Body", inserted: "Ursprünglich neu gespeicherte Records",
          members: "Belegte ursprüngliche Mitglieder", sequence: "Record-Sequenz", tick: "Geräte-Tick (ms)", missing: "Nicht dokumentiert",
          present: "Ursprüngliche Timescale-Zuordnung dokumentiert.", absent: "Bei Aufnahme keine Timescale-Zuordnung ausgewählt.",
          selection: "Der Zuordnungsstatus betrifft nur die damalige Timescale-Auswahl, nicht die Existenz eines Kontos oder Standorts.",
          qualified: "Feldoberfläche bei Aufnahme qualifiziert.", unqualified: "Feldoberfläche bei Aufnahme nicht qualifiziert.",
          field: "Dieser Feldstatus bewertet weder Messgültigkeit noch Kalibrierung.",
          tickNote: "Der Geräte-Tick ist keine UTC-Zeit und kein Sensoralter.",
          note: "Nur private gespeicherte Integritätsmetadaten: keine Regelwirkung, Kalibrierung, EC25, Export- oder Retentionsfreigabe und kein Nachweis vollständiger physischer Historie.",
          clocks: { monotonic_uptime: "Monotoner Geräte-Tick", source_utc: "Deklarierte Quell-UTC", server_receipt: "Server-Empfang" },
     },
     it: {
          aria: "FEEDER evidenze originali", title: "Evidenza originale", load: "Vedi metadati originali",
          loading: "Lettura dei metadati originali …", auth: "Accedi per vedere i metadati originali privati.",
          ready: "Completa prima l’accesso all’account.", timeout: "La lettura dei metadati originali ha richiesto troppo tempo.",
          unavailable: "Nessun metadato associato in modo verificabile a questa evidenza originale.",
          error: "Non è stato possibile leggere in modo verificabile i metadati originali.",
          record: "ID record originale", receipt: "ID ricevuta originale", utc: "Ricezione originale (UTC)",
          clock: "Base temporale della ricevuta", count: "Record nel corpo originale", inserted: "Record originariamente salvati come nuovi",
          members: "Membri originali documentati", sequence: "Sequenza record", tick: "Tick dispositivo (ms)", missing: "Non documentato",
          present: "Associazione Timescale originale documentata.", absent: "Nessuna associazione Timescale selezionata all’acquisizione.",
          selection: "Lo stato riguarda solo la selezione Timescale originale, non l’esistenza di un account o sito.",
          qualified: "Superficie dei campi qualificata all’acquisizione.", unqualified: "Superficie dei campi non qualificata all’acquisizione.",
          field: "Questo stato dei campi non valuta la validità della misura né la calibrazione.",
          tickNote: "Il tick dispositivo non è una data UTC né l’età del sensore.",
          note: "Solo metadati privati di integrità salvati: nessun controllo, calibrazione, EC25, autorizzazione all’esportazione o conservazione, né prova di cronologia fisica completa.",
          clocks: { monotonic_uptime: "Tick dispositivo monotono", source_utc: "UTC della fonte dichiarata", server_receipt: "Ricezione server" },
     },
     en: {
          aria: "FEEDER original evidence", title: "Original evidence", load: "View original metadata",
          loading: "Reading original metadata …", auth: "Sign in to view private original metadata.",
          ready: "Complete account access first.", timeout: "Reading the original metadata took too long.",
          unavailable: "No reliably associated metadata is available for this original evidence.",
          error: "The original metadata could not be read reliably.",
          record: "Original record ID", receipt: "Original receipt ID", utc: "Original receipt (UTC)",
          clock: "Receipt clock basis", count: "Records in original body", inserted: "Originally newly stored records",
          members: "Documented original members", sequence: "Record sequence", tick: "Device tick (ms)", missing: "Not documented",
          present: "Original Timescale association documented.", absent: "No Timescale association selected at acquisition.",
          selection: "This status concerns only the original Timescale selection, not the existence of an account or site.",
          qualified: "Field surface qualified at acquisition.", unqualified: "Field surface not qualified at acquisition.",
          field: "This field status evaluates neither measurement validity nor calibration.",
          tickNote: "The device tick is not UTC time or sensor age.",
          note: "Private stored integrity metadata only: no control, calibration, EC25, export or retention authorization, or proof of complete physical history.",
          clocks: { monotonic_uptime: "Monotonic device tick", source_utc: "Declared source UTC", server_receipt: "Server receipt" },
     },
}
const message = error => error?.status === 401
     ? "auth" : error?.status === 403 ? "ready" : error?.name === "TimeoutError" ? "timeout"
     : error?.status === 404 ? "unavailable" : "error"

// The unchanged public transport owns the closed DTO/session checks. Copy only
// display primitives and its six false grants; never retain raw/UID/Binding.
// The validated EC-context anchor supplies the ORIGINAL Record PK string,
// not a Sample PK inference. scopeRef is local race context, NOT ACL.
const display = (doc, recordId) => {
     if (doc?.version !== 1 || doc.visibility !== "private" || doc.source?.record_id !== recordId
          || !grants.every(key => doc[key] === false)) throw new Error("Original metadata unavailable")
     return Object.freeze({
          recordId: doc.source.record_id, sequence: doc.source.sequence, tick: doc.source.device_tick_ms,
          receiptId: doc.receipt.batch_receipt_id, receivedAt: doc.receipt.received_at,
          clockBasis: doc.receipt.clock_basis, recordCount: doc.receipt.record_count,
          insertedCount: doc.receipt.inserted_count, memberCount: doc.receipt.original_member_count,
          selection: doc.selection_state, qualified: doc.wire.field_surface_qualified,
          grants: Object.freeze(Object.fromEntries(grants.map(key => [key, doc[key]]))),
     })
}

export default function AeroponicOriginalRecordEvidence({ recordId, scopeRef, language = "de" }) {
     const text = TEXT[language] || TEXT.de
     const request = useRef({ mounted: false, epoch: 0, controller: null, pending: false,
          recordId: null, scopeRef: null })
     const [view, setView] = useState(null)
     useLayoutEffect(() => {
          const owner = request.current
          owner.mounted = true
          owner.recordId = recordId
          owner.scopeRef = scopeRef
          owner.epoch += 1
          owner.pending = false
          owner.controller = null
          setView(null)
          return () => {
               owner.mounted = false
               owner.epoch += 1
               owner.pending = false
               owner.controller?.abort()
               owner.controller = null
          }
     }, [recordId, scopeRef])

     const load = async () => {
          const owner = request.current
          if (!owner.mounted || owner.pending || !validRecordId(recordId) || !validScope(scopeRef)
               || owner.recordId !== recordId || owner.scopeRef !== scopeRef) return
          const controller = new AbortController(), epoch = ++owner.epoch
          owner.controller = controller
          owner.pending = true // Synchronous admission before React state/await.
          const current = () => owner.mounted && owner.epoch === epoch
               && owner.controller === controller && !controller.signal.aborted
               && owner.recordId === recordId && owner.scopeRef === scopeRef
          const value = { recordId, scopeRef, epoch, status: "loading" }
          setView(value)
          try {
               const doc = await loadAeroponicOriginalRecordMetadata(recordId, { signal: controller.signal })
               if (current()) setView({ ...value, status: "loaded", metadata: display(doc, recordId) })
          } catch (error) {
               if (current()) setView(error?.name === "AbortError" ? null
                    : { ...value, status: "error", message: message(error) })
          } finally {
               if (current()) {
                    owner.pending = false
                    owner.controller = null
               }
          }
     }
     const active = view?.recordId === recordId && view?.scopeRef === scopeRef
          && view?.epoch === request.current.epoch ? view : null
     const metadata = active?.metadata
     return <section aria-label={text.aria} aria-busy={active?.status === "loading"}>
          <h3>FEEDER · {text.title}</h3>
          <button type="button" disabled={!validRecordId(recordId) || !validScope(scopeRef)
               || active?.status === "loading"} onClick={load}>{text.load}</button>
          {active?.status === "loading" && <p role="status">{text.loading}</p>}
          {active?.status === "error" && <p role="alert">{text[active.message]}</p>}
          {metadata && <>
               <dl>
                    <dt>{text.record}</dt><dd>{metadata.recordId}</dd>
                    <dt>{text.receipt}</dt><dd>{metadata.receiptId}</dd>
                    <dt>{text.utc}</dt><dd>{metadata.receivedAt}</dd>
                    <dt>{text.clock}</dt><dd>{text.clocks[metadata.clockBasis]}</dd>
                    <dt>{text.count}</dt><dd>{metadata.recordCount}</dd>
                    <dt>{text.inserted}</dt><dd>{metadata.insertedCount}</dd>
                    <dt>{text.members}</dt><dd>{metadata.memberCount}</dd>
                    <dt>{text.sequence}</dt><dd>{metadata.sequence}</dd>
                    <dt>{text.tick}</dt><dd>{metadata.tick === null ? text.missing : metadata.tick}</dd>
               </dl>
               <p>{metadata.selection === "PRESENT" ? text.present : text.absent}</p>
               <p>{text.selection}</p>
               <p>{metadata.qualified ? text.qualified : text.unqualified}</p>
               <p>{text.field}</p>
               <p>{text.tickNote}</p>
               <p>{text.note}</p>
          </>}
     </section>
}
